import { useId, useMemo } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import {
  DENSITY_COMFORTABLE_DESCRIPTION,
  DENSITY_COMFORTABLE_LABEL,
  DENSITY_COMPACT_DESCRIPTION,
  DENSITY_COMPACT_LABEL,
  TABLE_SETTINGS_MIN_COLUMNS_TOOLTIP,
  TABLE_SETTINGS_TITLE,
} from "./messages";
import { MIN_COLUMNS_TARGET_CLASS } from "./table-settings-dialog.constants";
import type {
  TableDensity,
  TableSettingsDialogProps,
  TableSettingsDialogViewModel,
} from "./types-table-settings-dialog";

const NO_LABELS = {};
const NO_COLUMNS: readonly never[] = [];

/** ViewModel do TableSettingsDialog: opções de densidade, linhas de coluna e handlers de Aplicar/Cancelar; sem JSX. */
export function useTableSettingsDialog<C extends string>({
  dialog,
  columnLabels = NO_LABELS as Record<C, string>,
  mainColumns = NO_COLUMNS,
  showColumnsSection = false,
  lockedColumnsTooltip = TABLE_SETTINGS_MIN_COLUMNS_TOOLTIP,
  lockedColumnsDisabled = false,
  title = TABLE_SETTINGS_TITLE,
  testId,
  onApplied,
  className,
  style,
  classNames,
  ...props
}: TableSettingsDialogProps<C>): TableSettingsDialogViewModel<C> {
  const rest = useStableRest(props);
  const headingId = useId();
  const { draft, setDraftDensity, toggleDraftColumn, apply, cancel } = dialog;

  return useMemo(
    () => ({
      rest,
      testId,
      dialogTestId: `${testId}-dialog`,
      footerTestId: `${testId}-footer`,
      applyTestId: `${testId}-apply`,
      densityTestId: `${testId}-density`,
      columnsTestId: `${testId}-columns`,
      className,
      style,
      classNames,
      title,
      isOpen: dialog.isOpen,
      cancel,
      apply: () => {
        apply();
        onApplied?.();
      },
      densityTitleId: `${headingId}-density`,
      columnsTitleId: `${headingId}-columns`,
      density: draft.density,
      onDensityChange: (value: TableDensity) => setDraftDensity(value),
      // Ids dos `<input>` iguais aos do shared (`-density-confortavel|compacto`).
      densityOptions: [
        {
          value: "normal" as const,
          id: `${testId}-density-confortavel`,
          titleId: `${testId}-density-confortavel-title`,
          descriptionId: `${testId}-density-confortavel-description`,
          testId: `${testId}-density-item-normal`,
          label: DENSITY_COMFORTABLE_LABEL,
          description: DENSITY_COMFORTABLE_DESCRIPTION,
        },
        {
          value: "small" as const,
          id: `${testId}-density-compacto`,
          titleId: `${testId}-density-compacto-title`,
          descriptionId: `${testId}-density-compacto-description`,
          testId: `${testId}-density-item-small`,
          label: DENSITY_COMPACT_LABEL,
          description: DENSITY_COMPACT_DESCRIPTION,
        },
      ],
      showColumnsSection,
      minColumnsTooltipTarget: `.${MIN_COLUMNS_TARGET_CLASS}`,
      lockedColumnsTooltip,
      columns: (Object.keys(columnLabels) as C[]).map((column) => {
        const isMain = mainColumns.includes(column);
        return {
          column,
          id: `${testId}-column-${column}`,
          label: columnLabels[column],
          locked: isMain,
          disabled: isMain && lockedColumnsDisabled,
          targetClassName: isMain ? MIN_COLUMNS_TARGET_CLASS : undefined,
          checked: isMain || draft.visibleColumns.includes(column),
          onChange: () => toggleDraftColumn(column),
        };
      }),
    }),
    [
      title,
      dialog.isOpen,
      cancel,
      apply,
      onApplied,
      headingId,
      draft,
      setDraftDensity,
      toggleDraftColumn,
      testId,
      showColumnsSection,
      columnLabels,
      mainColumns,
      lockedColumnsTooltip,
      lockedColumnsDisabled,
      rest,
      className,
      style,
      classNames,
    ],
  );
}
